import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import {
  Target,
  Trophy,
  Plus,
  Search,
  User,
  Calendar,
  TrendingUp,
  Award,
  Flame,
  Footprints,
  Dumbbell,
  CalendarCheck,
  Crown,
  Medal,
  ChevronRight,
  Loader2,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  X,
} from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

// Badge icon mapping
const BADGE_ICONS = {
  footprints: Footprints,
  dumbbell: Dumbbell,
  target: Target,
  flame: Flame,
  trophy: Trophy,
  "calendar-check": CalendarCheck,
  award: Award,
  crown: Crown,
  medal: Medal,
};

// Badge color mapping
const BADGE_COLORS = {
  green: "bg-green-100 text-green-700 border-green-300",
  blue: "bg-blue-100 text-blue-700 border-blue-300",
  purple: "bg-purple-100 text-purple-700 border-purple-300",
  orange: "bg-orange-100 text-orange-700 border-orange-300",
  gold: "bg-yellow-100 text-yellow-700 border-yellow-300",
  teal: "bg-teal-100 text-teal-700 border-teal-300",
};

export default function GoalsModule({ businessId, user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages", "qr", "evaluations"]);

  const [activeTab, setActiveTab] = useState("goals");
  const [members, setMembers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [systemBadges, setSystemBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberGoals, setMemberGoals] = useState([]);
  const [memberBadges, setMemberBadges] = useState([]);
  const [showMemberModal, setShowMemberModal] = useState(false);
  
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({
    title: "",
    description: "",
    goal_type: "sessions",
    target_value: 4,
    period: "weekly",
  });
  
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [badgeForm, setBadgeForm] = useState({
    name: "",
    description: "",
    icon: "trophy",
    color: "gold",
  });
  
  const [saving, setSaving] = useState(false);
  const [deleteGoalDialog, setDeleteGoalDialog] = useState({ open: false, goalId: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [membersRes, goalsRes, badgesRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/members`, { credentials: "include", headers }),
        fetch(`${API}/goals/business/${businessId}`, { credentials: "include", headers }),
        fetch(`${API}/goals/badges/system`, { credentials: "include", headers }),
      ]);
      
      if (membersRes.ok) setMembers(await membersRes.json());
      if (goalsRes.ok) setGoals(await goalsRes.json());
      if (badgesRes.ok) setSystemBadges(await badgesRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberDetails = async (member) => {
    setSelectedMember(member);
    setShowMemberModal(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [goalsRes, badgesRes] = await Promise.all([
        fetch(`${API}/goals/business/${businessId}?member_id=${member.member_id}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/goals/badges/member/${member.member_id}`, {
          credentials: "include",
          headers,
        }),
      ]);
      
      if (goalsRes.ok) setMemberGoals(await goalsRes.json());
      if (badgesRes.ok) setMemberBadges(await badgesRes.json());
    } catch (error) {
      console.error("Error fetching member details:", error);
    }
  };

  const handleOpenGoalModal = (member, existingGoal = null) => {
    setSelectedMember(member);
    if (existingGoal) {
      setEditingGoal(existingGoal);
      setGoalForm({
        title: existingGoal.title,
        description: existingGoal.description || "",
        goal_type: existingGoal.goal_type,
        target_value: existingGoal.target_value,
        period: existingGoal.period,
      });
    } else {
      setEditingGoal(null);
      setGoalForm({
        title: "",
        description: "",
        goal_type: "sessions",
        target_value: 4,
        period: "weekly",
      });
    }
    setShowGoalModal(true);
  };

  const handleSaveGoal = async () => {
    if (!goalForm.title.trim()) {
      toast.error(t("evaluations:toast_hedefBasligiGerekli"));
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = editingGoal
        ? `${API}/goals/${editingGoal.goal_id}`
        : `${API}/goals/`;
      
      const response = await fetch(url, {
        method: editingGoal ? "PUT" : "POST",
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...goalForm,
          member_id: selectedMember.member_id,
        }),
      });
      
      if (response.ok) {
        toast.success(editingGoal ? t("evaluations:goalUpdated") : t("evaluations:goalCreated"));
        setShowGoalModal(false);
        fetchData();
        if (showMemberModal) {
          fetchMemberDetails(selectedMember);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    setDeleteGoalDialog({ open: true, goalId });
  };

  const confirmDeleteGoal = async () => {
    const goalId = deleteGoalDialog.goalId;
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/goals/${goalId}`, {
        method: "DELETE",
        credentials: "include",
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success(t('common:goalCancelled'));
        setDeleteGoalDialog({ open: false, goalId: null });
        fetchData();
        if (showMemberModal && selectedMember) {
          fetchMemberDetails(selectedMember);
        }
      } else {
        toast.error("Hedef silinemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeleting(false);
    }
  };

  const handleAwardBadge = async () => {
    if (!badgeForm.name.trim() || !badgeForm.description.trim()) {
      toast.error(t("common:toast_rozetAdiVeAciklamasiGerekli"));
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/goals/badges/member/${selectedMember.member_id}/award`, {
        method: "POST",
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(badgeForm),
      });
      
      if (response.ok) {
        toast.success("Rozet verildi!");
        setShowBadgeModal(false);
        setBadgeForm({ name: "", description: "", icon: "trophy", color: "gold" });
        fetchMemberDetails(selectedMember);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const filteredMembers = members.filter((m) =>
    `${m.name} ${m.surname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getGoalProgress = (goal) => {
    return Math.min(100, Math.round((goal.current_value / goal.target_value) * 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">Hedefler & Rozetler</h1>
          <p className="text-grey mt-1">
            Üyelerinize hedef belirleyin ve başarı rozetleri verin
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-64 mb-4">
          <TabsTrigger value="goals" className="rounded-xl">
            <Target className="w-4 h-4 mr-2" />
            Hedefler
          </TabsTrigger>
          <TabsTrigger value="badges" className="rounded-xl">
            <Trophy className="w-4 h-4 mr-2" />
            Rozetler
          </TabsTrigger>
        </TabsList>

        {/* GOALS TAB */}
        <TabsContent value="goals" className="space-y-6">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
            <Input
              placeholder={t('members:searchMember')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>

          {/* Members List */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Üyeler ({filteredMembers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredMembers.length === 0 ? (
                <p className="text-center py-8 text-grey">{t("common:memberNotFound")}</p>
              ) : (
                <div className="space-y-3">
                  {filteredMembers.map((member) => {
                    const memberGoalCount = goals.filter(g => g.member_id === member.member_id).length;
                    const activeGoalCount = goals.filter(g => g.member_id === member.member_id && g.status === "active").length;
                    
                    return (
                      <div
                        key={member.member_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
                        onClick={() => fetchMemberDetails(member)}
                        data-testid={`member-${member.member_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-navy" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-navy">
                                {member.name} {member.surname}
                              </p>
                              {member.is_app_registered === false && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-600 border-orange-300 bg-orange-50">
                                  Kayıt Olmayan
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-grey">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {activeGoalCount > 0 && (
                            <Badge className="bg-green-100 text-green-700">
                              <Target className="w-3 h-3 mr-1" />
                              {activeGoalCount} aktif hedef
                            </Badge>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenGoalModal(member);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Hedef Ekle
                          </Button>
                          <ChevronRight className="w-5 h-5 text-grey" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Goals Overview */}
          {goals.filter(g => g.status === "active").length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Aktif Hedefler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {goals.filter(g => g.status === "active").slice(0, 5).map((goal) => (
                    <div key={goal.goal_id} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-navy">{goal.title}</p>
                          <p className="text-sm text-grey">{goal.member_name}</p>
                        </div>
                        <Badge className={goal.period === "weekly" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                          {goal.period === "weekly" ? t("common:weekly") : t("common:monthly")}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{goal.current_value} / {goal.target_value}</span>
                          <span>{getGoalProgress(goal)}%</span>
                        </div>
                        <Progress value={getGoalProgress(goal)} className="h-2" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* BADGES TAB */}
        <TabsContent value="badges" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-600" />
                Sistem Rozetleri
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-grey mb-4">
                Bu rozetler otomatik olarak kazanılır
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {systemBadges.map((badge) => {
                  const Icon = BADGE_ICONS[badge.icon] || Trophy;
                  const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.gold;
                  
                  return (
                    <div
                      key={badge.badge_id}
                      className={`p-4 rounded-xl border-2 ${colorClass} text-center`}
                    >
                      <Icon className="w-8 h-8 mx-auto mb-2" />
                      <p className="font-medium text-sm">{badge.name}</p>
                      <p className="text-xs mt-1 opacity-75">{badge.description}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Member Detail Modal */}
      <Dialog open={showMemberModal} onOpenChange={setShowMemberModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-navy" />
              {selectedMember?.name} {selectedMember?.surname}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMember && (
            <div className="py-4 space-y-6">
              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => handleOpenGoalModal(selectedMember)}
                  className="bg-navy hover:bg-navy/90 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Hedef
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowBadgeModal(true)}
                  className="rounded-xl"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Rozet Ver
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('auth_token');
                      const response = await fetch(`${API}/goals/badges/sync/${selectedMember.member_id}`, {
                        method: "POST",
                        credentials: "include",
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                      });
                      if (response.ok) {
                        const data = await response.json();
                        toast.success(data.message);
                        // Refresh member badges
                        fetchMemberDetails(selectedMember);
                      } else {
                        toast.error(t("common:toast_rozetSenkronizasyonuBasarisiz"));
                      }
                    } catch (error) {
                      toast.error(t('common:errorOccurred'));
                    }
                  }}
                  className="rounded-xl text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Rozetleri Senkronize Et
                </Button>
              </div>

              {/* Goals Section */}
              <div>
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Hedefler ({memberGoals.length})
                </h3>
                {memberGoals.length === 0 ? (
                  <p className="text-sm text-grey py-4 text-center bg-slate-50 rounded-xl">
                    Henüz hedef belirlenmemiş
                  </p>
                ) : (
                  <div className="space-y-3">
                    {memberGoals.map((goal) => (
                      <div key={goal.goal_id} className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-navy">{goal.title}</p>
                            {goal.description && (
                              <p className="text-sm text-grey">{goal.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={
                              goal.status === "completed" ? "bg-green-100 text-green-700" :
                              goal.status === "active" ? "bg-blue-100 text-blue-700" :
                              "bg-grey-100 text-grey-700"
                            }>
                              {goal.status === "completed" ? t('common:completed') :
                               goal.status === "active" ? t("admin:active") : t("reservations:cancelledRes")}
                            </Badge>
                            {goal.status === "active" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500"
                                onClick={() => handleDeleteGoal(goal.goal_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{goal.current_value} / {goal.target_value} {goal.goal_type === "sessions" ? "seans" : ""}</span>
                            <span>{getGoalProgress(goal)}%</span>
                          </div>
                          <Progress 
                            value={getGoalProgress(goal)} 
                            className={`h-2 ${goal.status === "completed" ? "[&>div]:bg-green-500" : ""}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Badges Section */}
              <div>
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Rozetler ({memberBadges.length})
                </h3>
                {memberBadges.length === 0 ? (
                  <p className="text-sm text-grey py-4 text-center bg-slate-50 rounded-xl">
                    Henüz rozet kazanılmamış
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {memberBadges.map((badge) => {
                      const Icon = BADGE_ICONS[badge.badge_icon] || Trophy;
                      const colorClass = BADGE_COLORS[badge.badge_color] || BADGE_COLORS.gold;
                      
                      return (
                        <div
                          key={badge.member_badge_id}
                          className={`p-3 rounded-xl border-2 ${colorClass} text-center`}
                        >
                          <Icon className="w-6 h-6 mx-auto mb-1" />
                          <p className="font-medium text-xs">{badge.badge_name}</p>
                          <p className="text-xs opacity-75 mt-0.5">
                            {format(new Date(badge.awarded_at), "d MMM", { locale: getDateLocale() })}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Goal Form Modal */}
      <Dialog open={showGoalModal} onOpenChange={setShowGoalModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingGoal ? t('common:editGoal') : t('common:setNewGoal')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Hedef Başlığı *</Label>
              <Input
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                placeholder={t("packages:ph_ornHaftalik4Seans")}
                className="rounded-xl mt-1"
              />
            </div>
            
            <div>
              <Label>{t('common:description')}</Label>
              <Textarea
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                placeholder={t("evaluations:ph_hedefHakkindaAciklama")}
                className="rounded-xl mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hedef Tipi</Label>
                <Select
                  value={goalForm.goal_type}
                  onValueChange={(v) => setGoalForm({ ...goalForm, goal_type: v })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sessions">{t("packages:seansSayisi")}</SelectItem>
                    <SelectItem value="attendance">{t("common:devamlilik")}</SelectItem>
                    <SelectItem value="custom">{t("common:private")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{t("evaluations:hedefDeger")}</Label>
                <Input
                  type="number"
                  value={goalForm.target_value}
                  onChange={(e) => setGoalForm({ ...goalForm, target_value: parseInt(e.target.value) || 0 })}
                  min="1"
                  className="rounded-xl mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Periyot</Label>
              <Select
                value={goalForm.period}
                onValueChange={(v) => setGoalForm({ ...goalForm, period: v })}
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">{t("common:weekly")}</SelectItem>
                  <SelectItem value="monthly">{t("common:monthly")}</SelectItem>
                  <SelectItem value="custom">{t("common:ozelSure")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoalModal(false)} className="rounded-xl">
              İptal
            </Button>
            <Button
              onClick={handleSaveGoal}
              disabled={saving || !goalForm.title.trim()}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingGoal ? t('settings:update') : t('settings:create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award Badge Modal */}
      <Dialog open={showBadgeModal} onOpenChange={setShowBadgeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-600" />
              Özel Rozet Ver
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Rozet Adı *</Label>
              <Input
                value={badgeForm.name}
                onChange={(e) => setBadgeForm({ ...badgeForm, name: e.target.value })}
                placeholder={t("common:ph_ornAyinSporcusu")}
                className="rounded-xl mt-1"
              />
            </div>
            
            <div>
              <Label>{t("common:descriptionRequired")}</Label>
              <Textarea
                value={badgeForm.description}
                onChange={(e) => setBadgeForm({ ...badgeForm, description: e.target.value })}
                placeholder="Bu rozet neden verildi?"
                className="rounded-xl mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("common:icon")}</Label>
                <Select
                  value={badgeForm.icon}
                  onValueChange={(v) => setBadgeForm({ ...badgeForm, icon: v })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trophy">🏆 Kupa</SelectItem>
                    <SelectItem value="medal">🥇 Madalya</SelectItem>
                    <SelectItem value="award">⭐ Yıldız</SelectItem>
                    <SelectItem value="crown">👑 Taç</SelectItem>
                    <SelectItem value="flame">🔥 Alev</SelectItem>
                    <SelectItem value="target">🎯 Hedef</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{t("common:color")}</Label>
                <Select
                  value={badgeForm.color}
                  onValueChange={(v) => setBadgeForm({ ...badgeForm, color: v })}
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gold">🟡 Altın</SelectItem>
                    <SelectItem value="blue">🔵 Mavi</SelectItem>
                    <SelectItem value="green">🟢 Yeşil</SelectItem>
                    <SelectItem value="purple">🟣 Mor</SelectItem>
                    <SelectItem value="orange">🟠 Turuncu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBadgeModal(false)} className="rounded-xl">
              İptal
            </Button>
            <Button
              onClick={handleAwardBadge}
              disabled={saving || !badgeForm.name.trim() || !badgeForm.description.trim()}
              className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trophy className="w-4 h-4 mr-2" />}
              Rozet Ver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Goal Confirmation Dialog */}
      <AlertDialog open={deleteGoalDialog.open} onOpenChange={(open) => !deleting && setDeleteGoalDialog({ open, goalId: open ? deleteGoalDialog.goalId : null })}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common:cancelGoal')}</AlertDialogTitle>
            <AlertDialogDescription>
              Bu hedefi iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={deleting}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteGoal}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {deleting ? t("common:deleting") : t("qr:yesCancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
